import React, { useState, useMemo } from "react";
import styled from "styled-components";
import { DataGrid } from "@mui/x-data-grid";
import { colors } from "../../../Shared/colors";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import Tooltip from "@mui/material/Tooltip";
import { 
  useGetMyCMPQuery, 
  useGetAllCMPQuery,
  useUpdateCMPRemarksMutation,
  useUpdateSBDMRemarksMutation,
  useRemoveFromCMPMutation 
} from "../../../features/cmpSlice";
import { UnifiedTaskTable } from "../../Telesales/Telesaleleads/Telesalesearchleads/UnifiedTaskTable";
import { decryptData } from "../../../services/crypto";
import moment from "moment";
import {
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  IconButton,
  Typography,
} from "@mui/material";
import { toast } from "react-hot-toast";

const Main = styled.div`
  padding: 2em;
  background: ${colors.inputWhite};
  min-height: 100vh;
`;

const Table = styled.div`
  background: white;
  border-radius: 8px;
  padding: 1.5em;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  margin-top: 1.5em;
`;

const Action = styled.div`
  display: flex;
  gap: 0.5em;
  align-items: center;
  justify-content: center;
`;

const MeetingCount = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px 12px;
  border-radius: 12px;
  font-weight: 600;
  font-size: 0.875rem;
  background-color: ${props => {
    if (props.count === 0) return '#f1f5f9';
    if (props.count <= 2) return '#fef3c7';
    if (props.count <= 5) return '#fde68a';
    return '#fcd34d';
  }};
  color: ${props => {
    if (props.count === 0) return '#64748b';
    if (props.count <= 2) return '#92400e';
    if (props.count <= 5) return '#78350f';
    return '#713f12';
  }};
`;

const RemarksCell = styled.div`
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: pointer;
  &:hover {
    color: ${colors.brown};
  }
`;

const HeaderSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5em;
  padding: 1em;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
`;


const EmptyState = styled.div`
  text-align: center;
  padding: 4em 2em;
  .empty-icon {
    font-size: 4rem;
    margin-bottom: 1rem;
  }
  h3 {
    color: ${colors.textMed};
    margin-bottom: 0.5rem;
  }
  p {
    color: ${colors.textLight};
  }
`;

export const CMPDataTable = ({ filters = {} }) => {
  const [taskPopUp, setTaskPopUp] = useState(false);
  const [selectedLeadId, setSelectedLeadId] = useState(null);
  const [selectedLeadNumber, setSelectedLeadNumber] = useState(null);
  const [editOwnRemarksDialog, setEditOwnRemarksDialog] = useState(false);
  const [selectedOwnRemarksCMP, setSelectedOwnRemarksCMP] = useState(null);
  const [ownRemarks, setOwnRemarks] = useState("");
  const [editRemarksDialog, setEditRemarksDialog] = useState(false);
  const [selectedCMP, setSelectedCMP] = useState(null);
  const [sbdmRemarks, setSbdmRemarks] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedDeleteCMPId, setSelectedDeleteCMPId] = useState(null);

  // Get user role
  let role = null;
  const storedStaffRole = localStorage.getItem("role");
  if (storedStaffRole) {
    try {
      role = decryptData(storedStaffRole);
    } catch (error) {
      console.error("Failed to decrypt role:", error);
    }
  }

  const isAdminOrCEO = role === "ceo" || role === "admin";
  const isSBDM = role === "sbdm" || isAdminOrCEO;
  const loggedInUserId = (() => {
    const storedStaffId = localStorage.getItem("id");
    if (!storedStaffId) return null;
    try {
      return decryptData(storedStaffId);
    } catch (error) {
      return null;
    }
  })();

  const currentMonth = filters.month || "";
  const currentYear = filters.year || "";

  // Use appropriate query based on role
  const { data: myCMPData, isFetching: isMyCMPLoading } = useGetMyCMPQuery(
    { 
      month: currentMonth, 
      year: currentYear,
      project: filters.project || "",
      clientId: filters.clientId || "",
      phoneNumber: filters.phoneNumber || "",
      leadId: filters.leadId || ""
    },
    { skip: isAdminOrCEO }
  );

  const { data: allCMPData, isFetching: isAllCMPLoading } = useGetAllCMPQuery(
    { 
      month: currentMonth, 
      year: currentYear,
      employeeId: filters.employeeId || "",
      project: filters.project || "",
      clientId: filters.clientId || "",
      phoneNumber: filters.phoneNumber || "",
      leadId: filters.leadId || ""
    },
    { skip: !isAdminOrCEO }
  );

  const [updateCMPRemarks, { isLoading: isUpdatingOwnRemarks }] = useUpdateCMPRemarksMutation();
  const [updateSBDMRemarks, { isLoading: isUpdatingRemarks }] = useUpdateSBDMRemarksMutation();
  const [removeFromCMP, { isLoading: isRemoving }] = useRemoveFromCMPMutation();

  const cmpData = useMemo(() => {
    if (isAdminOrCEO) {
      return Array.isArray(allCMPData?.cmpData) ? allCMPData.cmpData : [];
    }
    return Array.isArray(myCMPData?.cmpData) ? myCMPData.cmpData : [];
  }, [isAdminOrCEO, allCMPData, myCMPData]);

  const isFetching = isAdminOrCEO ? isAllCMPLoading : isMyCMPLoading;

  const handleEditSBDMRemarks = (cmp) => {
    setSelectedCMP(cmp);
    setSbdmRemarks(cmp.sbdmRemarks || "");
    setEditRemarksDialog(true);
  };

  const handleEditOwnRemarks = (cmp) => {
    const ownerId = cmp?.employeeID?._id?.toString?.() || "";
    if (!loggedInUserId || ownerId !== loggedInUserId.toString()) return;
    setSelectedOwnRemarksCMP(cmp);
    setOwnRemarks(cmp.remarks || "");
    setEditOwnRemarksDialog(true);
  };

  const handleSaveOwnRemarks = async () => {
    if (!selectedOwnRemarksCMP) return;
    try {
      await updateCMPRemarks({
        cmpId: selectedOwnRemarksCMP._id,
        remarks: ownRemarks,
      }).unwrap();
      toast.success("Remarks updated successfully");
      setEditOwnRemarksDialog(false);
      setSelectedOwnRemarksCMP(null);
      setOwnRemarks("");
    } catch (error) {
      toast.error(error?.data?.message || "Failed to update remarks");
    }
  };

  const handleSaveSBDMRemarks = async () => {
    if (!selectedCMP) return;
    
    try {
      await updateSBDMRemarks({
        cmpId: selectedCMP._id,
        sbdmRemarks: sbdmRemarks,
      }).unwrap();
      toast.success("SBDM remarks updated successfully");
      setEditRemarksDialog(false);
      setSelectedCMP(null);
      setSbdmRemarks("");
    } catch (error) {
      toast.error(error?.data?.message || "Failed to update remarks");
    }
  };

  const openDeleteDialog = (cmpId) => {
    setSelectedDeleteCMPId(cmpId);
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    if (isRemoving) return;
    setDeleteDialogOpen(false);
    setSelectedDeleteCMPId(null);
  };

  const handleRemoveFromCMP = async () => {
    if (!selectedDeleteCMPId) return;
    try {
      await removeFromCMP(selectedDeleteCMPId).unwrap();
      toast.success("Lead removed from CMP successfully");
      closeDeleteDialog();
    } catch (error) {
      toast.error(error?.data?.message || "Failed to remove from CMP");
    }
  };

  const closeTaskDialog = () => {
    setTaskPopUp(false);
    setSelectedLeadId(null);
    setSelectedLeadNumber(null);
  };

  const closeRemarksDialog = () => {
    setEditRemarksDialog(false);
    setSelectedCMP(null);
    setSbdmRemarks("");
  };

  const closeOwnRemarksDialog = () => {
    setEditOwnRemarksDialog(false);
    setSelectedOwnRemarksCMP(null);
    setOwnRemarks("");
  };


  const columns = [
    {
      field: "salesPerson",
      headerName: "Sales Person",
      width: 150,
      renderCell: (params) => {
        return (
          <div>
            {params.row.employeeID?.firstName || ""} {params.row.employeeID?.lastName || ""}
          </div>
        );
      },
    },
    {
      field: "contactNo",
      headerName: "Contact No",
      width: 130,
      renderCell: (params) => {
        const phoneNo = params.row.leadID?.clientID?.cellNo?.[0]?.phoneNo;
        return phoneNo ? (
          <a href={`tel:+${phoneNo}`} style={{ color: colors.textBlue }}>
            {phoneNo}
          </a>
        ) : (
          <span style={{ color: colors.textLight }}>N/A</span>
        );
      },
    },
    {
      field: "clientName",
      headerName: "Client Name",
      width: 180,
      renderCell: (params) => {
        return (
          <Tooltip title={params.row.leadID?.clientID?.fullName || ""}>
            <div style={{ 
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap"
            }}>
              {params.row.leadID?.clientID?.fullName || ""}
            </div>
          </Tooltip>
        );
      },
    },
    {
      field: "project",
      headerName: "Project",
      width: 150,
      renderCell: (params) => {
        return (
          <Tooltip title={params.row.leadID?.leadOpportunity?.project?.projectName || ""}>
            <div style={{ 
              color: colors.textBlue,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap"
            }}>
              {params.row.leadID?.leadOpportunity?.project?.projectName || ""}
            </div>
          </Tooltip>
        );
      },
    },
    {
      field: "meetingDate",
      headerName: "Meeting Date",
      width: 130,
      renderCell: (params) => {
        return params.row.meetingDate ? (
          <div>{moment(params.row.meetingDate).format("DD-MMM-YYYY")}</div>
        ) : (
          <span style={{ color: colors.textLight }}>Not Set</span>
        );
      },
    },
    {
      field: "salesPersonMeetingsCount",
      headerName: "Sales Person Meetings Count",
      width: 200,
      renderCell: (params) => {
        const count = params.row.salesPersonMeetingsCount || 0;
        return (
          <MeetingCount count={count}>
            {count}
          </MeetingCount>
        );
      },
    },
    {
      field: "bdmMeetingsCount",
      headerName: "BDM Meetings Count",
      width: 170,
      renderCell: (params) => {
        const count = params.row.bdmMeetingsCount || 0;
        return (
          <MeetingCount count={count}>
            {count}
          </MeetingCount>
        );
      },
    },
    {
      field: "remarks",
      headerName: "Remarks",
      width: 200,
      renderCell: (params) => {
        const remarks = params.row.remarks || "";
        const ownerId = params.row.employeeID?._id?.toString?.() || "";
        const canEditOwnRemarks =
          Boolean(loggedInUserId) && ownerId === loggedInUserId.toString();
        return (
          <Tooltip title={remarks || "No remarks"}>
            <RemarksCell
              style={{ cursor: canEditOwnRemarks ? "pointer" : "default" }}
              onClick={() => canEditOwnRemarks && handleEditOwnRemarks(params.row)}
            >
              {remarks || <span style={{ color: colors.textLight }}>No remarks</span>}
            </RemarksCell>
          </Tooltip>
        );
      },
    },
    {
      field: "sbdmRemarks",
      headerName: "SBDM's Remarks",
      width: 200,
      renderCell: (params) => {
        const remarks = params.row.sbdmRemarks || "";
        return (
          <div style={{ display: "flex", alignItems: "center", gap: "0.5em" }}>
            <Tooltip title={remarks || "No remarks"}>
              <RemarksCell style={{ flex: 1 }}>
                {remarks || <span style={{ color: colors.textLight }}>No remarks</span>}
              </RemarksCell>
            </Tooltip>
            {isSBDM && (
              <EditIcon
                style={{ 
                  color: colors.brown, 
                  cursor: "pointer",
                  fontSize: "1.2rem"
                }}
                onClick={() => handleEditSBDMRemarks(params.row)}
              />
            )}
          </div>
        );
      },
    },
    {
      field: "action",
      headerName: "Action",
      width: 120,
      sortable: false,
      filterable: false,
      hideable: false,
      renderCell: (params) => {
        const canRemove =
          Boolean(loggedInUserId) &&
          (params.row.employeeID?._id?.toString?.() || params.row.employeeID?._id?.toString() || "") ===
            loggedInUserId.toString();
        
        return (
          <Action>
            <Tooltip title="View Tasks">
              <RemoveRedEyeIcon
                style={{ color: colors.brown, cursor: "pointer" }}
                onClick={() => {
                  setTaskPopUp(true);
                  setSelectedLeadId(params.row.leadID?._id);
                  setSelectedLeadNumber(params.row.leadID?.leadID);
                }}
              />
            </Tooltip>
            {canRemove && (
              <Tooltip title="Remove from CMP">
                <DeleteIcon
                  style={{ color: "#ef4444", cursor: "pointer" }}
                  onClick={() => openDeleteDialog(params.row._id)}
                />
              </Tooltip>
            )}
          </Action>
        );
      },
    },
  ];

  const rows = useMemo(() => {
    return cmpData.map((cmp, index) => ({
      id: cmp._id || index,
      ...cmp,
    }));
  }, [cmpData]);

  return (
    <Main>
      <HeaderSection>
        <div>
          <h2 style={{ margin: 0, color: colors.textMed }}>
            Monthly Pipeline - {moment().month(currentMonth - 1).format("MMMM")} {currentYear}
          </h2>
          <p style={{ margin: "0.5em 0 0 0", color: colors.textLight }}>
            Total Leads in Pipeline: {rows.length}
          </p>
        </div>
      </HeaderSection>

      <Table>
        {rows.length > 0 ? (
          <DataGrid
            rows={rows}
            columns={columns}
            pageSize={50}
            rowsPerPageOptions={[25, 50, 100]}
            pagination
            loading={isFetching}
            autoHeight
            sx={{
              border: "1px solid #e0e0e0",
              '& .MuiDataGrid-cell': {
                borderBottom: '1px solid #f0f0f0',
              },
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: '#f8f9fa',
                borderBottom: '2px solid #e0e0e0',
                fontWeight: 600,
              },
              '& .MuiDataGrid-row:hover': {
                backgroundColor: '#f8f9fa',
              },
            }}
          />
        ) : (
          <EmptyState>
            <div className="empty-icon">📊</div>
            <h3>No Leads in Pipeline</h3>
            <p>
              {isFetching
                ? "Loading CMP data..."
                : "No leads have been added to the monthly pipeline yet. Add leads from the Search Leads page."}
            </p>
          </EmptyState>
        )}
      </Table>

      {/* Task View Modal */}
      <Dialog
        open={taskPopUp}
        onClose={closeTaskDialog}
        fullWidth
        maxWidth="lg"
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: "hidden",
            boxShadow: "0 24px 60px rgba(15, 23, 42, 0.25)",
          },
        }}
      >
        <DialogTitle
          sx={{
            borderBottom: "1px solid #e5e7eb",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            py: 1.5,
          }}
        >
          <div>
            <Typography sx={{ fontWeight: 700, color: colors.textMed, fontSize: "1rem" }}>
              Lead Tasks
            </Typography>
            {selectedLeadNumber && (
              <Typography sx={{ fontSize: "0.8rem", color: colors.textLight }}>
                Lead ID: {selectedLeadNumber}
              </Typography>
            )}
          </div>
          <IconButton size="small" onClick={closeTaskDialog} aria-label="Close task modal">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: "1rem 1.25rem", minHeight: { xs: "60vh", md: "70vh" } }}>
          <UnifiedTaskTable leadID={selectedLeadId} leadNumber={selectedLeadNumber} />
        </DialogContent>
      </Dialog>

      {/* SBDM Remarks Edit Dialog */}
      <Dialog
        open={editOwnRemarksDialog}
        onClose={closeOwnRemarksDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: "0 20px 50px rgba(15, 23, 42, 0.2)",
          },
        }}
      >
        <DialogTitle
          sx={{
            borderBottom: "1px solid #e5e7eb",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            py: 1.5,
          }}
        >
          <Typography sx={{ fontWeight: 700, color: colors.textMed, fontSize: "1rem" }}>
            Update Remarks
          </Typography>
          <IconButton size="small" onClick={closeOwnRemarksDialog} aria-label="Close remarks modal">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mt: 1, mb: 1.5, color: colors.textLight }}>
            Update your own CMP remarks for this lead.
          </DialogContentText>
          <TextField
            fullWidth
            multiline
            rows={6}
            label="Remarks"
            value={ownRemarks}
            onChange={(e) => setOwnRemarks(e.target.value)}
            margin="normal"
            placeholder="Enter remarks..."
            inputProps={{ maxLength: 1000 }}
            helperText={`${ownRemarks.length}/1000 characters`}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeOwnRemarksDialog} disabled={isUpdatingOwnRemarks} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={handleSaveOwnRemarks}
            variant="contained"
            disabled={isUpdatingOwnRemarks}
            sx={{ background: colors.brown, "&:hover": { background: "#1a6b6b" } }}
          >
            {isUpdatingOwnRemarks ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* SBDM Remarks Edit Dialog */}
      <Dialog
        open={editRemarksDialog}
        onClose={closeRemarksDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: "0 20px 50px rgba(15, 23, 42, 0.2)",
          },
        }}
      >
        <DialogTitle
          sx={{
            borderBottom: "1px solid #e5e7eb",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            py: 1.5,
          }}
        >
          <Typography sx={{ fontWeight: 700, color: colors.textMed, fontSize: "1rem" }}>
            Update SBDM Remarks
          </Typography>
          <IconButton size="small" onClick={closeRemarksDialog} aria-label="Close remarks modal">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mt: 1, mb: 1.5, color: colors.textLight }}>
            Add clear and actionable remarks for this lead. These notes help management track conversion readiness.
          </DialogContentText>
          <TextField
            fullWidth
            multiline
            rows={6}
            label="SBDM Remarks"
            value={sbdmRemarks}
            onChange={(e) => setSbdmRemarks(e.target.value)}
            margin="normal"
            placeholder="Enter your remarks about this lead..."
            inputProps={{ maxLength: 1000 }}
            helperText={`${sbdmRemarks.length}/1000 characters`}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeRemarksDialog} disabled={isUpdatingRemarks} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={handleSaveSBDMRemarks}
            variant="contained"
            disabled={isUpdatingRemarks}
            sx={{ background: colors.brown, "&:hover": { background: "#1a6b6b" } }}
          >
            {isUpdatingRemarks ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={closeDeleteDialog}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: "0 20px 50px rgba(15, 23, 42, 0.2)",
          },
        }}
      >
        <DialogTitle sx={{ pb: 0.5 }}>
          <Typography sx={{ fontWeight: 700, color: "#b91c1c", fontSize: "1rem" }}>
            Remove Lead From CMP?
          </Typography>
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: colors.textMed }}>
            This lead will be removed from the current monthly pipeline view. You can add it again later if needed.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeDeleteDialog} variant="outlined" disabled={isRemoving}>
            Cancel
          </Button>
          <Button
            onClick={handleRemoveFromCMP}
            variant="contained"
            color="error"
            disabled={isRemoving}
          >
            {isRemoving ? "Removing..." : "Remove"}
          </Button>
        </DialogActions>
      </Dialog>
    </Main>
  );
};
